import { useQuery } from "@tanstack/react-query";
import { Trophy, TrendingUp, Timer } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const PODIUM_COLORS: Record<number, string> = {
  1: "#FFD700",
  2: "#C0C0C0",
  3: "#CD7F32",
};

export function PerformanceKPIs({ userId }: { userId: string }) {
  const { data: bestPosition } = useQuery({
    queryKey: ["kpi-best-position", userId],
    queryFn: async () => {
      const { data: memberships } = await supabase
        .from("league_members")
        .select("league_id")
        .eq("user_id", userId);
      if (!memberships?.length) return null;

      let best: number | null = null;
      for (const m of memberships) {
        const { data: standings } = await supabase.rpc("get_league_standings", {
          p_league_id: m.league_id,
        });
        if (!standings) continue;
        // Skip leagues where everyone has 0 points
        const hasResults = standings.some((s: any) => s.total_points > 0);
        if (!hasResults) continue;
        const rank = standings.findIndex((s: any) => s.user_id === userId) + 1;
        if (rank > 0 && (best === null || rank < best)) best = rank;
      }
      return best;
    },
  });

  const { data: correctTips } = useQuery({
    queryKey: ["kpi-correct-tips", userId],
    queryFn: async () => {
      // Count correct matchup picks
      const { count: matchupCorrect, error } = await supabase
        .from("picks")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("is_correct", true);
      if (error) throw error;

      // Count correct pole & sprint picks from race_picks
      const { data: racePicks, error: rpErr } = await supabase
        .from("race_picks")
        .select("pole_is_correct, sprint_p1_correct, sprint_p2_correct, sprint_p3_correct")
        .eq("user_id", userId);
      if (rpErr) throw rpErr;

      let raceCorrect = 0;
      for (const rp of racePicks ?? []) {
        if (rp.pole_is_correct) raceCorrect++;
        if (rp.sprint_p1_correct) raceCorrect++;
        if (rp.sprint_p2_correct) raceCorrect++;
        if (rp.sprint_p3_correct) raceCorrect++;
      }

      return (matchupCorrect ?? 0) + raceCorrect;
    },
  });

  const { data: poleMargin } = useQuery({
    queryKey: ["kpi-pole-margin", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("race_picks")
        .select("predicted_pole_margin, actual_pole_margin")
        .eq("user_id", userId)
        .not("actual_pole_margin", "is", null);
      if (error) throw error;
      if (!data?.length) return null;
      const total = data.reduce((sum, r) => {
        return sum + Math.abs((r.predicted_pole_margin ?? 0) - (r.actual_pole_margin ?? 0));
      }, 0);
      return total / 1000;
    },
  });

  const posLabel = bestPosition != null ? `P${bestPosition}` : "—";
  const posColor = bestPosition && bestPosition <= 3 ? PODIUM_COLORS[bestPosition] : undefined;
  const tipsVal = correctTips ?? 0;
  const marginVal = poleMargin ?? 0;
  const marginStr = marginVal.toFixed(3);
  const marginGood = poleMargin != null && poleMargin < 1.0;

  return (
    <div className="grid grid-cols-3 gap-2 md:gap-4">
      <KPICard
        icon={<Trophy className="h-4 w-4 md:h-5 md:w-5" style={{ color: "#FFD700" }} />}
        value={posLabel}
        label="Best League Position"
        valueColor={posColor}
      />
      <KPICard
        icon={<TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-green-500" />}
        value={String(tipsVal)}
        label="Correct Tips"
      />
      <KPICard
        icon={<Timer className="h-4 w-4 md:h-5 md:w-5 text-purple-500" />}
        value={marginStr}
        label="Pole Margin (s)"
        valueClassName={marginGood ? "text-green-500" : undefined}
      />
    </div>
  );
}

function KPICard({
  icon,
  value,
  label,
  valueColor,
  valueClassName,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  valueColor?: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-lg border bg-card p-3 md:p-5 text-center">
      {icon}
      <span
        className={valueClassName ?? "text-foreground"}
        style={valueColor ? { color: valueColor } : undefined}
      >
        <span className="text-xl md:text-2xl font-bold">{value}</span>
      </span>
      <span className="text-[10px] md:text-xs text-muted-foreground leading-tight">{label}</span>
    </div>
  );
}
